// Screen bodies, shared verbatim by the mobile and desktop shells.

import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight, Check, ChevronDown, ChevronRight, ChevronUp, Lightbulb,
  MessageSquare, BookOpen, FileCheck, Settings as SettingsIcon, Upload, X,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Badge, Button, Console, Display, Field, IconButton, Kicker, Progress,
  Rule, Segmented, Switch, inputStyle,
} from "./ui.jsx";
import { THEMES, FONTS } from "./theme.js";
import { derivePassword } from "./api.js";

export const TAB_ICONS = {
  counsel: MessageSquare,
  assoclaws: BookOpen,
  resolutions: FileCheck,
  settings: SettingsIcon,
};

export const TABS = [
  { id: "counsel", num: "01", label: "Counsel" },
  { id: "assoclaws", num: "02", label: "Laws" },
  { id: "resolutions", num: "03", label: "Resolutions" },
  { id: "settings", num: "04", label: "Settings" },
];

export const SUGGESTIONS = [
  "Can the board stop me renting my unit?",
  "What are the quiet hours here?",
  "Can I install solar panels on my roof?",
  "What happens if my dues are late?",
  "How do I dispute a fine?",
];

const Markdown = ({ children }) => (
  <div style={{ font: "400 15px/1.6 var(--bq-font-body)", color: "var(--ink)", textWrap: "pretty" }}>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{children || ""}</ReactMarkdown>
  </div>
);

/* ── composer ─────────────────────────────────────────────────────────── */

export function Composer({ value, onChange, onSend, onStop, pending, placeholder, rows = 3 }) {
  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onSend(); }
  };
  return (
    <div style={{ flex: "none", borderTop: "1px solid var(--ink)", background: "var(--surface)", padding: "10px 20px 12px" }}>
      <div style={{ display: "flex", alignItems: "stretch", border: "1px solid var(--ink)", background: "var(--bg)" }}>
        <textarea
          value={value}
          rows={rows}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={onKeyDown}
          style={{
            flex: 1, minWidth: 0, border: "none", outline: "none", background: "transparent",
            resize: "none", padding: "11px 12px", minHeight: 92, color: "var(--ink)",
            font: "400 13px/1.5 var(--bq-font-mono)",
          }}
        />
        <button
          onClick={pending ? onStop : onSend}
          aria-label={pending ? "Stop" : "Send"}
          style={{
            flex: "none", width: 52, alignSelf: "stretch", border: "none",
            borderLeft: "1px solid var(--ink)", background: "var(--accent)",
            display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
          }}
        >
          {pending
            ? <span style={{ width: 14, height: 14, background: "var(--onAccent)", display: "block" }} />
            : <ArrowRight size={18} color="var(--onAccent)" strokeWidth={2.5} />}
        </button>
      </div>
    </div>
  );
}

/* ── chat thread ──────────────────────────────────────────────────────── */

function Sources({ sources }) {
  const [open, setOpen] = useState(false);
  if (!sources || sources.length === 0) return null;
  return (
    <>
      <Button variant="secondary" onClick={() => setOpen((v) => !v)}
        style={{ alignSelf: "flex-start", padding: "7px 10px", minHeight: 34, font: "600 10px/1 var(--bq-font-display)" }}>
        {(open ? "Hide " : "Show ") + sources.length + " sources"}
      </Button>
      {open && (
        <div style={{ display: "flex", flexDirection: "column", background: "var(--surface)", borderLeft: "2px solid var(--accent)" }}>
          {sources.map((s) => (
            <div key={s.id} style={{ padding: "11px 13px", borderBottom: "1px solid var(--rule)" }}>
              <div style={{ font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".1em", textTransform: "uppercase", color: "var(--accentDeep)", marginBottom: 5 }}>
                {s.label}{s.document ? " · " + s.document : ""}
              </div>
              <div style={{ font: "400 12.5px/1.5 var(--bq-font-body)", color: "var(--body)" }}>{s.text}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export function ChatThread({ messages, pending, debugOn, debugLines, emptyCopy }) {
  const scrollRef = useRef(null);
  // Keep the newest turn in view without scrollIntoView (which can fight the shell).
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, pending]);

  return (
    <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "0 20px 16px" }}>
      {messages.length === 0 && !pending && (
        <div style={{ borderTop: "1px solid var(--ink)", paddingTop: 14 }}>
          <p style={{ margin: 0, font: "400 15px/1.5 var(--bq-font-body)", color: "var(--body)", textWrap: "pretty" }}>{emptyCopy}</p>
        </div>
      )}

      {messages.map((m) => (
        <div key={m.id} style={{ padding: "0 0 22px" }}>
          {m.role === "user" && (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <div style={{ maxWidth: "84%", background: "var(--accent)", color: "var(--onAccent)", padding: "11px 13px", font: "500 14.5px/1.45 var(--bq-font-body)" }}>
                {m.text}
              </div>
            </div>
          )}

          {m.role === "assistant" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 10, borderTop: "1px solid var(--ink)", paddingTop: 12 }}>
              <Kicker tone="muted">Counsel</Kicker>
              <Markdown>{m.text}</Markdown>
              {m.unverified && (
                <div style={{ background: "var(--accentTint)", borderLeft: "2px solid var(--accent)", padding: "10px 12px" }}>
                  <span style={{ font: "400 12px/1.5 var(--bq-font-body)", color: "var(--accentInk)" }}>
                    Answered from general Davis-Stirling knowledge — no passage in your documents supports this directly.
                  </span>
                </div>
              )}
              <Sources sources={m.sources} />
            </div>
          )}

          {m.role === "error" && (
            <div style={{ borderTop: "1px solid var(--accent)", paddingTop: 12 }}>
              <Kicker>Request failed</Kicker>
              <p style={{ margin: "6px 0 0", font: "400 13px/1.5 var(--bq-font-body)", color: "var(--accentDeep)" }}>{m.text}</p>
            </div>
          )}
        </div>
      ))}

      {pending && (
        <div style={{ borderTop: "1px solid var(--ink)", paddingTop: 12, display: "flex", flexDirection: "column", gap: 9 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div style={{ width: 14, height: 14, background: "var(--accent)", animation: "bq-pulse 1s ease-in-out infinite" }} />
            <span style={{ font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".14em", textTransform: "uppercase" }}>Searching</span>
          </div>
          <div style={{ height: 2, background: "var(--rule)", overflow: "hidden" }}>
            <div style={{ height: 2, background: "var(--accent)", transformOrigin: "left", animation: "bq-bar 1.6s linear forwards" }} />
          </div>
        </div>
      )}

      {debugOn && debugLines.length > 0 && (
        <div style={{ paddingTop: 8 }}>
          <Console title="Chat debug console" lines={debugLines} open onToggle={() => {}} maxHeight={140} />
        </div>
      )}
    </div>
  );
}

/* ── suggestions panel ────────────────────────────────────────────────── */

export function SuggestionList({ items, onPick, title = "Residents usually ask" }) {
  return (
    <>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, padding: "14px 20px 10px" }}>
        <Kicker>{title}</Kicker>
        <span style={{ font: "400 10px/1 var(--bq-font-display)", letterSpacing: ".08em", textTransform: "uppercase", color: "var(--muted)" }}>Tap to send</span>
      </div>
      <div style={{ borderTop: "1px solid var(--rule)" }}>
        {items.map((text) => (
          <button key={text} onClick={() => onPick(text)}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
              width: "100%", textAlign: "left", border: "none", borderBottom: "1px solid var(--rule)",
              background: "transparent", color: "var(--ink)", padding: "15px 20px", minHeight: 54,
              font: "500 15px/1.3 var(--bq-font-body)", cursor: "pointer",
            }}>
            <span>{text}</span>
            <ArrowRight size={16} color="var(--accent)" strokeWidth={2.5} style={{ flex: "none" }} />
          </button>
        ))}
      </div>
    </>
  );
}

/* ── association laws ─────────────────────────────────────────────────── */

export function DocumentBrowser({ documents, activeDocId, onSelectDoc, mode, onMode, sections, filter, onFilter, onAskSection }) {
  const [openId, setOpenId] = useState(null);
  const active = documents.find((d) => d.id === activeDocId) || documents[0];

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div style={{ flex: "none", display: "flex", padding: "0 20px", borderBottom: "1px solid var(--ink)", overflowX: "auto" }}>
        {documents.map((d) => {
          const on = active && d.id === active.id;
          return (
            <button key={d.id} onClick={() => onSelectDoc(d.id)}
              style={{
                flex: "none", border: "none", background: "transparent", cursor: on ? "default" : "pointer",
                borderBottom: on ? "2px solid var(--accent)" : "none", marginBottom: on ? -1 : 0,
                padding: "10px 14px 8px 0", whiteSpace: "nowrap",
                font: (on ? "700 " : "500 ") + "13px/1 var(--bq-font-display)",
                color: on ? "var(--ink)" : "var(--muted)",
              }}>{d.title || d.filename}</button>
          );
        })}
      </div>

      <div style={{ flex: "none", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "10px 20px", background: "var(--surface)", borderBottom: "1px solid var(--rule)" }}>
        <span style={{ font: "400 10px/1.3 var(--bq-font-display)", letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted2)", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {active ? (active.section_count || sections.length) + " sections · " + active.filename : "No documents indexed"}
        </span>
        <Segmented
          value={mode}
          onChange={onMode}
          options={[{ value: "browse", label: "Browse" }, { value: "ask", label: "Ask" }]}
        />
      </div>

      {mode === "browse" && (
        <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <div style={{ padding: "12px 20px 8px" }}>
            <input value={filter} onChange={(e) => onFilter(e.target.value)} placeholder="Filter sections" style={{ ...inputStyle, font: "400 13px/1.2 var(--bq-font-body)" }} />
          </div>
          {sections.map((s) => {
            const open = openId === s.id;
            return (
              <div key={s.id} style={{ borderBottom: "1px solid var(--rule)", margin: "0 20px" }}>
                <button onClick={() => setOpenId(open ? null : s.id)}
                  style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", border: "none", background: "transparent", padding: "13px 0", minHeight: 48, cursor: "pointer" }}>
                  <span style={{ flex: "none", width: 28, font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".06em", color: "var(--accent)" }}>{s.number}</span>
                  <span style={{ flex: 1, minWidth: 0, font: "600 13.5px/1.3 var(--bq-font-display)", color: "var(--ink)" }}>{s.title}</span>
                  {open ? <ChevronUp size={14} color="var(--ink)" strokeWidth={2.5} /> : <ChevronDown size={14} color="var(--muted)" strokeWidth={2.5} />}
                </button>
                {open && (
                  <div style={{ padding: "0 0 16px 40px", borderLeft: "2px solid var(--accent)", marginLeft: 4, display: "flex", flexDirection: "column", gap: 10 }}>
                    <Markdown>{s.text}</Markdown>
                    <Button variant="secondary" onClick={() => onAskSection(s)}
                      style={{ alignSelf: "flex-start", padding: "7px 10px", minHeight: 34, font: "600 10px/1 var(--bq-font-display)" }}>
                      Ask about this §
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
          {sections.length === 0 && (
            <p style={{ margin: 20, font: "400 13.5px/1.5 var(--bq-font-body)", color: "var(--muted)" }}>
              No sections match that filter.
            </p>
          )}
          <div style={{ height: 16 }} />
        </div>
      )}
    </div>
  );
}

/* ── resolutions ──────────────────────────────────────────────────────── */

export function ResolutionsDashboard({ resolutions, filter, onFilter, onOpen, loading }) {
  return (
    <div style={{ flex: 1, minHeight: 0, overflow: "auto", paddingTop: 12 }}>
      <div style={{ borderTop: "1px solid var(--ink)", margin: "0 20px" }}>
        {resolutions.map((r) => (
          <button key={r.id} onClick={() => onOpen(r.id)}
            style={{
              display: "flex", flexDirection: "column", alignItems: "stretch", gap: 6, width: "100%",
              textAlign: "left", border: "none", borderBottom: "1px solid var(--rule)",
              background: "transparent", padding: "14px 2px", cursor: "pointer",
            }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
              <span style={{ font: "600 11px/1 var(--bq-font-display)", letterSpacing: ".08em", color: "var(--muted2)" }}>{r.resolution_number}</span>
              {r.status === "approved"
                ? <Badge tone="solid"><Check size={10} strokeWidth={3.5} />Approved</Badge>
                : <Badge tone="outline">Draft</Badge>}
            </div>
            <span style={{ font: "600 15px/1.25 var(--bq-font-display)", color: "var(--ink)" }}>{r.title}</span>
            <span style={{ font: "400 11.5px/1 var(--bq-font-body)", color: "var(--muted)" }}>
              {new Date(r.created_at).toLocaleDateString()} · drafted by {r.created_by || "—"}
            </span>
          </button>
        ))}
      </div>
      {!loading && resolutions.length === 0 && (
        <p style={{ margin: 20, font: "400 13.5px/1.5 var(--bq-font-body)", color: "var(--muted)" }}>
          {filter === "draft" ? "No drafts in progress." : filter === "approved" ? "Nothing approved yet." : "No resolutions on record."}
        </p>
      )}
      <div style={{ height: 16 }} />
    </div>
  );
}

export function ResolutionDetail({ resolution, onBack, onApprove, onDelete, busy }) {
  const [signName, setSignName] = useState("");
  const [error, setError] = useState("");
  if (!resolution) return null;
  const isDraft = resolution.status === "draft";
  const canSign = signName.trim().length >= 3 && !busy;

  const sign = async () => {
    setError("");
    try { await onApprove(resolution.id, signName.trim()); setSignName(""); }
    catch (err) { setError(err.message); }
  };

  return (
    <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 20px 12px" }}>
        <Button variant="ghost" onClick={onBack} style={{ gap: 6 }}>
          <ChevronRight size={14} strokeWidth={2.5} style={{ transform: "rotate(180deg)" }} />All resolutions
        </Button>
        {isDraft ? <Badge tone="outline">Draft</Badge> : <Badge tone="solid"><Check size={10} strokeWidth={3.5} />Approved</Badge>}
      </div>

      <div style={{ padding: "0 20px 20px", display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <Kicker style={{ marginBottom: 6 }}>{resolution.resolution_number}</Kicker>
          <h3 style={{ margin: "0 0 6px", font: "800 24px/1.05 var(--bq-font-display)", letterSpacing: "-0.015em", textWrap: "pretty" }}>{resolution.title}</h3>
          <div style={{ font: "400 11.5px/1.4 var(--bq-font-body)", color: "var(--muted)" }}>
            {new Date(resolution.created_at).toLocaleDateString()} · drafted by {resolution.created_by || "—"}
          </div>
        </div>
        <Rule strong />
        <div style={{ font: "400 13.5px/1.6 var(--bq-font-mono)", color: "var(--ink)", whiteSpace: "pre-wrap" }}>{resolution.text}</div>

        {isDraft && (
          <>
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="secondary" onClick={() => onDelete(resolution.id)} style={{ padding: "9px 12px", minHeight: 44, font: "600 10px/1 var(--bq-font-display)" }}>
                Delete draft
              </Button>
            </div>
            <div style={{ border: "1px solid var(--accent)", padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
              <span style={{ font: "700 12px/1 var(--bq-font-display)", letterSpacing: ".1em", textTransform: "uppercase" }}>Approve &amp; sign</span>
              <p style={{ margin: 0, font: "400 12.5px/1.5 var(--bq-font-body)", color: "var(--body)" }}>
                Signing is final. An approved resolution cannot be edited or deleted, and your name is timestamped into the audit trail.
              </p>
              <input value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="Type your full name to sign" style={inputStyle} />
              {error && <span style={{ font: "400 12px/1.4 var(--bq-font-body)", color: "var(--accentDeep)" }}>{error}</span>}
              <Button onClick={sign} disabled={!canSign}>{busy ? "Signing…" : "Approve & sign"}</Button>
            </div>
          </>
        )}

        {resolution.signatures && resolution.signatures.length > 0 && (
          <div>
            <Kicker tone="muted" style={{ marginBottom: 8 }}>Signatures</Kicker>
            <div style={{ borderTop: "1px solid var(--rule)" }}>
              {resolution.signatures.map((s, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 9, padding: "11px 0", borderBottom: "1px solid var(--rule)" }}>
                  <Check size={14} color="var(--accent)" strokeWidth={3} />
                  <span style={{ flex: 1, font: "600 13px/1.2 var(--bq-font-display)" }}>{s.name}</span>
                  <span style={{ font: "400 11px/1 var(--bq-font-body)", color: "var(--muted)" }}>{new Date(s.signed_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {resolution.audit_log && resolution.audit_log.length > 0 && (
          <div>
            <Kicker tone="muted" style={{ marginBottom: 8 }}>Audit trail</Kicker>
            <div style={{ borderTop: "1px solid var(--rule)" }}>
              {resolution.audit_log.map((a, i) => (
                <div key={i} style={{ display: "flex", gap: 10, padding: "10px 0", borderBottom: "1px solid var(--rule)" }}>
                  <span style={{ flex: "none", width: 84, font: "400 10.5px/1.35 var(--bq-font-mono)", color: "var(--muted)" }}>
                    {new Date(a.at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </span>
                  <span style={{ flex: 1, font: "400 12px/1.35 var(--bq-font-body)", color: "var(--body)" }}>
                    {a.action}{a.actor ? " · " + a.actor : ""}{a.detail ? " — " + a.detail : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Drafting conversation; the draft itself lives in a bar that expands to a sheet. */
export function DraftPane({ turns, pending, draft, onSend, onPickOption, onOpenDraft, emptyCopy }) {
  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "14px 20px 18px", display: "flex", flexDirection: "column", gap: 18 }}>
        {turns.length === 0 && !pending && (
          <div style={{ borderTop: "1px solid var(--ink)", paddingTop: 14 }}>
            <Display size={20} style={{ marginBottom: 6 }}>Draft a resolution</Display>
            <p style={{ margin: 0, font: "400 14px/1.5 var(--bq-font-body)", color: "var(--body)", textWrap: "pretty" }}>{emptyCopy}</p>
          </div>
        )}

        {turns.map((t) => (
          <div key={t.id}>
            {t.role === "user" && (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <div style={{ maxWidth: "84%", background: "var(--accent)", color: "var(--onAccent)", padding: "11px 13px", font: "500 14.5px/1.45 var(--bq-font-body)" }}>{t.text}</div>
              </div>
            )}
            {t.role === "assistant" && (
              <div style={{ borderTop: "1px solid var(--ink)", paddingTop: 12, display: "flex", flexDirection: "column", gap: 10 }}>
                <Kicker tone="muted">Counsel</Kicker>
                <Markdown>{t.text}</Markdown>
                {t.options && t.options.choices && t.options.choices.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {t.options.choices.map((c, i) => (
                      <button key={c.label} onClick={() => onPickOption(c.label)}
                        style={{
                          display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4,
                          width: "100%", textAlign: "left", cursor: "pointer", padding: 12, minHeight: 48,
                          border: i === 0 ? "1px solid var(--accent)" : "1px solid var(--rule2)",
                          background: i === 0 ? "var(--accentTint)" : "transparent",
                        }}>
                        {i === 0 && <span style={{ font: "600 9px/1 var(--bq-font-display)", letterSpacing: ".12em", textTransform: "uppercase", color: "var(--accentDeep)" }}>Recommended</span>}
                        <span style={{ font: "600 14px/1.25 var(--bq-font-display)", color: "var(--ink)" }}>{c.label}</span>
                        {c.description && <span style={{ font: "400 12px/1.4 var(--bq-font-body)", color: "var(--muted2)" }}>{c.description}</span>}
                      </button>
                    ))}
                    <p style={{ margin: 0, font: "400 11.5px/1.4 var(--bq-font-body)", color: "var(--muted)" }}>Or type your own answer below.</p>
                  </div>
                )}
              </div>
            )}
            {t.role === "error" && (
              <p style={{ margin: 0, font: "400 13px/1.5 var(--bq-font-body)", color: "var(--accentDeep)" }}>{t.text}</p>
            )}
          </div>
        ))}

        {pending && (
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div style={{ width: 14, height: 14, background: "var(--accent)", animation: "bq-pulse 1s ease-in-out infinite" }} />
            <span style={{ font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".14em", textTransform: "uppercase" }}>Drafting</span>
          </div>
        )}
      </div>

      {draft.text && (
        <button onClick={onOpenDraft}
          style={{
            flex: "none", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
            width: "100%", border: "none", borderTop: "1px solid var(--ink)",
            background: "var(--ink)", color: "var(--bg)", padding: "12px 20px", cursor: "pointer", textAlign: "left",
          }}>
          <span style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
            <span style={{ font: "600 9px/1 var(--bq-font-display)", letterSpacing: ".14em", textTransform: "uppercase", color: "var(--accentSoft)" }}>
              Current draft · {draft.ready ? "Ready to save" : "In progress"}
            </span>
            <span style={{ font: "600 13px/1.2 var(--bq-font-display)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {draft.title || "Untitled resolution"}
            </span>
          </span>
          <ChevronUp size={16} strokeWidth={2.5} style={{ flex: "none" }} />
        </button>
      )}
    </div>
  );
}

export function DraftSheet({ draft, onChange, onSave, onClose, saving, createdBy, onCreatedBy }) {
  return (
    <>
      <div style={{ flex: "none", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "14px 20px 12px", borderBottom: "1px solid var(--ink)" }}>
        <div>
          <Kicker style={{ marginBottom: 5 }}>Current draft</Kicker>
          <div style={{ font: "800 20px/1 var(--bq-font-display)", letterSpacing: "-0.01em" }}>{draft.ready ? "Ready to save" : "In progress"}</div>
        </div>
        <IconButton label="Close draft" onClick={onClose} style={{ width: 36, height: 36, border: "1px solid var(--ink)" }}>
          <X size={14} strokeWidth={3} />
        </IconButton>
      </div>
      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "14px 20px 18px", display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ border: "1px solid var(--ink)", background: "var(--surface)", padding: 13 }}>
          <div style={{ font: "400 12.5px/1.6 var(--bq-font-mono)", whiteSpace: "pre-wrap", color: "var(--ink)" }}>{draft.text}</div>
        </div>
        <Field label="Title" hint="Suggested by Counsel">
          <input value={draft.title} onChange={(e) => onChange({ ...draft, title: e.target.value })} style={{ ...inputStyle, font: "600 14px/1.2 var(--bq-font-display)" }} />
        </Field>
        <Field label="Drafted by">
          <input value={createdBy} onChange={(e) => onCreatedBy(e.target.value)} placeholder="Board member name" style={inputStyle} />
        </Field>
        <Button onClick={onSave} disabled={saving || !draft.text}>{saving ? "Saving…" : "Save as draft"}</Button>
      </div>
    </>
  );
}

/* ── onboarding wizard ────────────────────────────────────────────────── */

const DropZone = ({ accept, onFile, title, hint, required }) => {
  const ref = useRef(null);
  const [over, setOver] = useState(false);
  const take = (file) => file && onFile(file);
  return (
    <>
      <button
        onClick={() => ref.current.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files[0]); }}
        style={{
          width: "100%", padding: "26px 16px", minHeight: 120, cursor: "pointer",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
          border: "1px dashed " + (required ? "var(--accent)" : "var(--rule2)"),
          background: over ? "var(--accentTint)" : "transparent",
        }}>
        <Upload size={28} color="var(--accent)" strokeWidth={2} />
        <span style={{ font: "700 14px/1.2 var(--bq-font-display)", color: "var(--ink)" }}>{title}</span>
        <span style={{ font: "400 11.5px/1.2 var(--bq-font-body)", color: "var(--muted)" }}>{hint}</span>
      </button>
      <input ref={ref} type="file" accept={accept} onChange={(e) => take(e.target.files[0])} style={{ display: "none" }} />
    </>
  );
};

const UploadRow = ({ upload }) => (
  <div style={{ border: "1px solid var(--ink)", background: "var(--surface)", padding: "12px 13px", display: "flex", flexDirection: "column", gap: 8 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <span style={{ flex: 1, minWidth: 0, font: "600 13px/1.2 var(--bq-font-display)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{upload.filename}</span>
      {upload.status === "indexed" && <Badge tone="solid"><Check size={10} strokeWidth={3.5} />Indexed</Badge>}
      {upload.status === "failed" && <Badge tone="outline">Failed</Badge>}
      {(upload.status === "uploading" || upload.status === "indexing") && (
        <Badge tone="outline" style={{ animation: "bq-pulse 1.2s ease-in-out infinite" }}>
          {upload.status === "uploading" ? "Uploading" : "Indexing"}
        </Badge>
      )}
    </div>
    <Progress pct={upload.status === "indexed" ? 100 : upload.pct} />
    <div style={{ font: "400 10.5px/1.3 var(--bq-font-display)", letterSpacing: ".06em", textTransform: "uppercase", color: "var(--muted2)" }}>
      {upload.label}{upload.sections ? " · " + upload.sections + " sections" : ""}{upload.error ? " · " + upload.error : ""}
    </div>
  </div>
);

export function OnboardingWizard({ bq, onClose, onFinish }) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState(bq.community?.name || "");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [docTitle, setDocTitle] = useState("");
  const [logOpen, setLogOpen] = useState(true);
  const [busy, setBusy] = useState(false);

  const ccrUploads = bq.uploads.filter((u) => u.label === "CC&Rs");
  const otherUploads = bq.uploads.filter((u) => u.label !== "CC&Rs");
  const ccrIndexed = ccrUploads.some((u) => u.status === "indexed");
  const steps = ["Community", "CC&Rs", "Documents"];
  const canNext = step === 0 ? name.trim().length > 1 : ccrIndexed;

  const next = async () => {
    if (step === 0) {
      setBusy(true);
      try { await bq.saveCommunityName(name.trim()); } catch { /* logged in console */ }
      setBusy(false);
    }
    setStep((s) => Math.min(2, s + 1));
  };

  return (
    <>
      <div style={{ flex: "none", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, padding: "14px 20px 12px" }}>
        <div>
          <Kicker style={{ marginBottom: 5 }}>First-time setup</Kicker>
          <div style={{ font: "800 22px/1 var(--bq-font-display)", letterSpacing: "-0.02em" }}>Onboard a community</div>
        </div>
        <IconButton label="Close onboarding" onClick={onClose} style={{ width: 36, height: 36, border: "1px solid var(--ink)" }}>
          <X size={14} strokeWidth={3} />
        </IconButton>
      </div>

      <div style={{ flex: "none", display: "grid", gridTemplateColumns: "repeat(3,1fr)", borderTop: "1px solid var(--ink)", borderBottom: "1px solid var(--ink)" }}>
        {steps.map((label, i) => {
          const on = step === i;
          return (
            <button key={label} onClick={() => setStep(i)}
              style={{
                minWidth: 0, border: "none", borderRight: "1px solid var(--rule)",
                background: on ? "var(--ink)" : "transparent", color: on ? "var(--bg)" : "var(--muted2)",
                display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 5,
                padding: "10px 11px 12px", minHeight: 52, cursor: "pointer",
              }}>
              <span style={{ font: "600 9px/1 var(--bq-font-display)", letterSpacing: ".1em", color: on ? "var(--accentSoft)" : "var(--rule2)" }}>{"0" + (i + 1)}</span>
              <span style={{ font: "700 12px/1.1 var(--bq-font-display)", letterSpacing: ".03em", textTransform: "uppercase", textAlign: "left" }}>{label}</span>
            </button>
          );
        })}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "16px 20px 20px", display: "flex", flexDirection: "column", gap: 18 }}>
        {step === 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <Display size={17} style={{ marginBottom: 5 }}>Community details</Display>
              <p style={{ margin: 0, font: "400 13px/1.5 var(--bq-font-body)", color: "var(--body)", textWrap: "pretty" }}>
                The name appears in every answer the assistant gives, and determines the community's password.
              </p>
            </div>
            <Field label="Community name">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Oakridge Estates" style={{ ...inputStyle, font: "600 15px/1.2 var(--bq-font-display)" }} />
            </Field>
            {name.trim() && (
              <div style={{ background: "var(--accentTint)", borderLeft: "2px solid var(--accent)", padding: "10px 12px" }}>
                <div style={{ font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".1em", textTransform: "uppercase", color: "var(--accentDeep)", marginBottom: 5 }}>Password</div>
                <div style={{ font: "500 13px/1.4 var(--bq-font-mono)", color: "var(--accentInk)", wordBreak: "break-all" }}>{derivePassword(name)}</div>
                <div style={{ font: "400 11.5px/1.4 var(--bq-font-body)", color: "var(--accentInk)", marginTop: 4 }}>Save this — board members need it to enter.</div>
              </div>
            )}
            <div style={{ display: "flex", gap: 10 }}>
              <Field label="City" style={{ flex: 1 }}>
                <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Sunnyvale" style={inputStyle} />
              </Field>
              <Field label="ZIP" style={{ width: 112, flex: "none" }}>
                <input value={zip} onChange={(e) => setZip(e.target.value)} placeholder="94086" style={inputStyle} />
              </Field>
            </div>
          </div>
        )}

        {step === 1 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <Display size={17} style={{ marginBottom: 5 }}>CC&amp;Rs</Display>
              <p style={{ margin: 0, font: "400 13px/1.5 var(--bq-font-body)", color: "var(--body)", textWrap: "pretty" }}>
                Required. A Markdown file with <code style={{ font: "400 12px/1 var(--bq-font-mono)" }}>## ARTICLE N</code> headers — the declaration is read before the statute on every answer.
              </p>
            </div>
            {!ccrIndexed && (
              <DropZone required accept=".md,text/markdown" onFile={(f) => bq.uploadCcrs(f)}
                title="Drop the CC&Rs here" hint="or click to browse · .md with ## ARTICLE headers" />
            )}
            {ccrUploads.map((u) => <UploadRow key={u.id} upload={u} />)}
          </div>
        )}

        {step === 2 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div>
              <Display size={17} style={{ marginBottom: 5 }}>Supporting documents</Display>
              <p style={{ margin: 0, font: "400 13px/1.5 var(--bq-font-body)", color: "var(--body)", textWrap: "pretty" }}>
                Bylaws, operating rules, anything else the board cites. Each is chunked, embedded and cited separately — add them now or later in Settings.
              </p>
            </div>
            <Field label="Document title" hint="Optional; defaults to the filename">
              <input value={docTitle} onChange={(e) => setDocTitle(e.target.value)} placeholder="Rules & Regulations 2024" style={inputStyle} />
            </Field>
            <DropZone accept=".md,.pdf,.docx,text/markdown" onFile={(f) => { bq.uploadDocument(f, docTitle || undefined); setDocTitle(""); }}
              title="Add a document" hint="or click to browse" />
            {otherUploads.map((u) => <UploadRow key={u.id} upload={u} />)}
            {bq.documents.length > 0 && (
              <div style={{ borderTop: "1px solid var(--rule)" }}>
                {bq.documents.map((d) => (
                  <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 0", borderBottom: "1px solid var(--rule)" }}>
                    <span style={{ flex: 1, minWidth: 0, font: "600 13px/1.2 var(--bq-font-display)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {d.title || d.filename}
                    </span>
                    <Button variant="ghost" onClick={() => bq.removeDocument(d.id)}>Remove</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <Console
          title="Upload console"
          endpoint={"POST " + (bq.community?.name ? "/community/ccrs" : "/community/name")}
          lines={bq.uploadLog}
          open={logOpen}
          onToggle={() => setLogOpen((v) => !v)}
        />
      </div>

      <div style={{ flex: "none", borderTop: "1px solid var(--ink)", background: "var(--surface)", padding: "12px 20px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        {step === 0
          ? <span style={{ font: "600 10px/1 var(--bq-font-display)", letterSpacing: ".1em", textTransform: "uppercase", color: "var(--rule2)" }}>Step 1 of 3</span>
          : <Button variant="ghost" onClick={() => setStep((s) => s - 1)}>Back</Button>}
        {step === 2
          ? <Button onClick={() => onFinish(name.trim())}>Finish setup</Button>
          : <Button onClick={next} disabled={!canNext || busy}>{step === 0 ? (busy ? "Saving…" : "Save & continue") : "Continue"}</Button>}
      </div>
    </>
  );
}

/* ── settings ─────────────────────────────────────────────────────────── */

export function SettingsPanel({ bq, theme, font, onTheme, onFont, debugOn, onToggleDebug, onOpenOnboarding, boardMember, onBoardMember, role = "board", managerSlot = null, residentSlot = null }) {
  const rows = role !== "board" ? [] : [
    { id: "assoc", label: "Association", value: (bq.community?.name || "Not set") + " · " + bq.documents.length + " documents · " + (bq.totalChunks || 0) + " sections" },
    { id: "docs", label: "Documents", value: bq.community?.ccrs_uploaded ? "CC&Rs on file · manage or replace" : "Onboard a community · upload governing documents" },
  ];

  return (
    <div style={{ flex: 1, minHeight: 0, overflow: "auto" }}>
      <div style={{ margin: "12px 20px 0" }}><Rule strong /></div>
      <div style={{ margin: "0 20px" }}>
        {rows.map((row) => (
          <button key={row.id} onClick={onOpenOnboarding}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
              width: "100%", textAlign: "left", border: "none", borderBottom: "1px solid var(--rule)",
              background: "transparent", padding: "15px 2px", minHeight: 48, cursor: "pointer",
            }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ font: "600 14px/1.2 var(--bq-font-display)", color: "var(--ink)" }}>{row.label}</div>
              <div style={{ font: "400 11.5px/1.35 var(--bq-font-body)", color: "var(--muted)", marginTop: 3 }}>{row.value}</div>
            </div>
            <ChevronRight size={15} color="var(--muted)" strokeWidth={2.5} style={{ flex: "none" }} />
          </button>
        ))}

        {role === "board" && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "15px 0", borderBottom: "1px solid var(--rule)" }}>
            <Field label={null} style={{ flex: 1 }}>
              <div style={{ font: "600 14px/1.2 var(--bq-font-display)" }}>Board member name</div>
              <input value={boardMember} onChange={(e) => onBoardMember(e.target.value)} placeholder="Used as 'drafted by'" style={{ ...inputStyle, marginTop: 6 }} />
            </Field>
          </div>
        )}

        {managerSlot}
        {residentSlot}

        <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "15px 0", borderBottom: "1px solid var(--rule)" }}>
          <div>
            <div style={{ font: "600 14px/1.2 var(--bq-font-display)" }}>Appearance</div>
            <div style={{ font: "400 11.5px/1.35 var(--bq-font-body)", color: "var(--muted)", marginTop: 3 }}>
              {(THEMES.find((t) => t.id === theme) || THEMES[0]).note}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8 }}>
            {THEMES.map((t) => {
              const on = t.id === theme;
              return (
                <button key={t.id} onClick={() => onTheme(t.id)}
                  style={{
                    border: on ? "1px solid var(--accent)" : "1px solid var(--rule2)",
                    background: on ? "var(--accentTint)" : "transparent",
                    padding: "9px 8px", minHeight: 56, cursor: on ? "default" : "pointer",
                    display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 7,
                  }}>
                  <span style={{ display: "flex", gap: 3 }}>
                    {t.swatches.map((c, i) => (
                      <span key={c} style={{ width: 12, height: 12, background: c, display: "block", border: i === 0 ? "1px solid var(--rule2)" : "none" }} />
                    ))}
                  </span>
                  <span style={{ font: "700 10px/1 var(--bq-font-display)", letterSpacing: ".06em", textTransform: "uppercase", color: on ? "var(--ink)" : "var(--muted2)", textAlign: "left" }}>
                    {t.label}
                  </span>
                </button>
              );
            })}
          </div>
          <Segmented style={{ alignSelf: "flex-start" }} value={font} onChange={onFont}
            options={FONTS.map((f) => ({ value: f.id, label: f.label }))} />
        </div>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "15px 0", borderBottom: "1px solid var(--rule)" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ font: "600 14px/1.2 var(--bq-font-display)" }}>Chat debug console</div>
            <div style={{ font: "400 11.5px/1.35 var(--bq-font-body)", color: "var(--muted)", marginTop: 3 }}>Timestamped request log under each answer</div>
          </div>
          <Switch on={debugOn} onChange={onToggleDebug} label="Chat debug console" />
        </div>
      </div>

      <div style={{ margin: "18px 20px", background: "var(--surface)", borderLeft: "2px solid var(--ink)", padding: "12px 13px" }}>
        <Kicker tone="muted" style={{ marginBottom: 6 }}>Where your data lives</Kicker>
        <p style={{ margin: 0, font: "400 12.5px/1.5 var(--bq-font-body)", color: "var(--body)" }}>
          Documents, resolutions and request tickets are stored on the association's server, so any board member can reload them. Appearance and chat history stay on this device.
        </p>
      </div>
      <div style={{ height: 10 }} />
    </div>
  );
}

export { Lightbulb };
